// Blog / Teachings content. Each post has a unique slug -> shareable at /insights/:slug
// The flagship framework uses typed blocks; the 30 Property Lay guides use markdown.
import { ARTICLES } from "./articles";

const FRAMEWORK_POSTS = [
  {
    slug: "why-buyers-who-love-the-property-still-dont-close",
    category: "Sales Framework",
    title: "Why Buyers Who Love the Property Still Don't Close",
    excerpt:
      "Emotional attachment to a property and readiness to transact are two different psychological states. This framework isolates where the real blocker sits - and how to resolve it.",
    readTime: "6 min read",
    date: "December 2025",
    author: "Sambhav K. Pandey",
    blocks: [
      { type: "h2", text: "Premise" },
      {
        type: "lead",
        text: "Emotional attachment to a property and readiness to transact are two separate psychological states. Sales teams conflate them and keep re-selling the property when the buyer has already \u201cbought\u201d it emotionally - the actual blocker is elsewhere. This framework isolates where the blocker sits.",
      },

      { type: "h2", text: "Stage 1: Diagnose - The Four Blockers" },
      {
        type: "p",
        text: "Every stalled deal falls into one (sometimes two) of these. Train your team to identify which one is live before attempting any close.",
      },
      {
        type: "blockers",
        items: [
          {
            n: "01",
            title: "Financial Ambiguity",
            body: "Buyer hasn't concretely mapped affordability - loan eligibility, down payment liquidity, EMI comfort - against their actual monthly cash flow. They know the number on paper but haven't stress-tested it against their life.",
            diagnostic: "\u201cHave you run this past your bank yet, or just estimated it?\u201d",
            stalls: "Buyers won't admit financial uncertainty directly - it feels like admitting they can't afford it. So it surfaces as \u201clet me think about it\u201d instead.",
          },
          {
            n: "02",
            title: "Decision Authority Gap",
            body: "The person in front of you isn't the sole decision-maker. Spouse, parent, or business partner hasn't seen the property or isn't aligned.",
            diagnostic: "\u201cWho else needs to walk through this with you before it's final?\u201d",
            stalls: "Sales reps pitch to the room, not to the absent decision-maker - so the real objection-holder never gets addressed.",
          },
          {
            n: "03",
            title: "Comparative Anchoring",
            body: "Buyer is actively evaluating 1\u20132 other projects and using your project as the benchmark, not the target.",
            diagnostic: "\u201cWhat else are you comparing this to right now?\u201d",
            stalls: "Reps assume love for the property = exclusivity of consideration. It rarely is, especially in the \u20b950L\u20132Cr segment.",
          },
          {
            n: "04",
            title: "Risk Perception (Trust Deficit)",
            body: "Not fear of the property - fear of the decision. Concerns about builder delivery history, RERA compliance, resale liquidity, possession delays.",
            diagnostic: "\u201cWhat would need to be true for you to feel 100% confident, not just 80%?\u201d",
            stalls: "This masquerades as repeated feature-questions (\u201ctell me about the amenities again\u201d) when the real issue is unresolved trust.",
          },
        ],
      },

      { type: "h2", text: "Stage 2: Match - Blocker-Specific Resolution Tactics" },
      {
        type: "table",
        head: ["Blocker", "Wrong Move (rep default)", "Right Move"],
        rows: [
          ["Financial Ambiguity", "Re-explaining price / payment plan", "Get them to a loan pre-approval call the same week - externalize the math"],
          ["Decision Authority Gap", "Pushing harder on the present buyer", "Schedule a second site visit specifically for the absent decision-maker"],
          ["Comparative Anchoring", "Bad-mouthing competitor projects", "Ask what the competitor project lacks that they noticed - let them self-articulate your edge"],
          ["Risk Perception", "More photos / brochures", "Third-party proof: RERA number, existing-owner testimonial call, builder's past possession record"],
        ],
      },

      { type: "h2", text: "Stage 3: Sequence - The Close Conversation Structure" },
      {
        type: "p",
        text: "Train reps to run this exact sequence, not a generic pitch:",
      },
      {
        type: "steps",
        items: [
          { title: "Confirm the love is real", body: "\u201cOn a scale of 1\u201310, how much do you like the property itself?\u201d Isolates product-fit from decision-readiness. If it's 8+, the objection is NOT the product." },
          { title: "Surface the blocker", body: "Use the four diagnostic questions above. Ask directly; buyers answer directly when asked directly - they rarely volunteer it." },
          { title: "Resolve only that blocker", body: "Don't re-pitch the whole property. Address the one gap named." },
          { title: "Set a dated next step", body: "Never end on \u201ctake your time.\u201d End on \u201cLet's get you the loan pre-approval by Thursday\u201d or \u201cLet's schedule the second visit with your wife for Saturday.\u201d" },
        ],
      },

      { type: "h2", text: "Core Training Principle" },
      {
        type: "callout",
        text: "A buyer who loves the property but doesn't close isn't undecided about the property - they're undecided about something else they haven't said out loud. Your job is not to sell harder. Your job is to ask better questions.",
      },

      { type: "h2", text: "Practice Drill for the Session" },
      {
        type: "p",
        text: "Role-play in pairs: one plays a buyer with a specific blocker (assign one of the four, don't tell the rep which). The rep must diagnose it within 3 questions before attempting any close language. Debrief on which diagnostic question worked and which didn't.",
      },
    ],
  },
];

// Normalise the 30 markdown guides into post objects
const GUIDE_POSTS = ARTICLES.map((a) => ({
  slug: a.slug,
  category: a.category,
  title: a.title,
  excerpt: a.meta,
  readTime: a.readTime,
  date: a.date,
  author: a.author,
  markdown: a.markdown,
}));

export const POSTS = [...FRAMEWORK_POSTS, ...GUIDE_POSTS];

export const CATEGORIES = ["All", "Sales Framework", "Guide", "Case Study"];

export const getPost = (slug) => POSTS.find((p) => p.slug === slug);
